import { PropertySlug, propertySlugs } from '@mgmresorts/web-utils';

import { ignoreParams } from './persistParameterContext';

export function isSSR() {
  return (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !document.body ||
    !!(window && (window as Window & { SSR_MODE?: boolean }).SSR_MODE)
  );
}

export function parseStringParam(param) {
  if (typeof param === 'undefined') {
    return param;
  }
  return Array.isArray(param) ? param[0] : param;
}

export function cleanPropertyParam(param?: string | string[]): PropertySlug | undefined {
  const property = (parseStringParam(param) || '').toLowerCase() as PropertySlug | 'www';

  return property === 'www' ? 'mgmresorts' : propertySlugs.includes(property) ? property : undefined;
}

export function getHost(): string {
  const host = window?.location?.host;

  return isSSR() || !host ? '' : host;
}

export function getOrigin(): string {
  let origin = window?.location?.origin;
  origin = origin?.includes('localhost')
    ? `https://${process.env.REACT_APP_ENVIRONMENT ?? 'dev'}.devtest.vegas`
    : origin;

  return isSSR() || !origin ? '' : origin;
}

export function getUrlParts(): { property?: PropertySlug; domain?: string; tld?: string } {
  const host = getHost();
  const parts = host.split('.');

  // if we have an odd URL, bail
  if (parts.length < 3 || parts.length > 4) {
    return { property: undefined, domain: undefined, tld: undefined };
  }

  if (['preview', 'preprod', 'dev', 'uat', 'qa3', 'qa4'].some(environment => parts[0].includes(environment))) {
    if (parts.length === 3) {
      const subParts = parts[0].split('-');

      if (subParts.length > 1) {
        return { property: cleanPropertyParam(subParts[1]), domain: parts[1], tld: parts[2] };
      }

      return { property: 'mgmresorts', domain: parts[1], tld: parts[2] };
    }

    // remove the proceeding portion of the URL (staging or preprod)
    parts.shift();
  }

  return { property: cleanPropertyParam(parts[0]), domain: parts[1], tld: parts[2] };
}

const parseParam = (param: string) => param?.toString();
const parseSessionParams = (sessionParams: string) => JSON.parse(sessionParams);

const getRawSessionParams = () => window.sessionStorage.getItem('mgm_signIn_params') || '{}';
const getSearchParams = () => parseSessionParams(getRawSessionParams());

/**
 * Helper function to return formatted URL params
 */
export const getUrlParams = () => {
  const searchParams = getSearchParams();
  const clientId = parseParam(searchParams?.client_id);
  const redirectUri = parseParam(searchParams?.redirect_uri);
  const codeChallenge = parseParam(searchParams?.code_challenge);
  const state = parseParam(searchParams?.state);

  return {
    clientId,
    redirectUri,
    codeChallenge,
    state,
  };
};

/**
 * Helper function to remove sensitive URL params
 * and ensure the redirect URL is safe.
 */
export const getSanitizedRedirectUri = (redirectUri: URL | string): URL | string => {
  const fallback = getOrigin() || 'https://mgmresorts.com';

  try {
    const baseOrigin = getOrigin() || window.location.origin;
    const url = new URL(redirectUri.toString(), baseOrigin);
    ignoreParams.forEach(param => url.searchParams.delete(param));

    return typeof redirectUri === 'string' ? url.toString() : url;
  } catch (e) {
    // On parse failure, do not return the raw, unvalidated redirectUri
    return fallback;
  }
};
